/**
 * ShapePanel — left-side shape picker that serves as the drag source for
 * Story 1.3 "拖拽生成立方体" (T-1.3.1).
 *
 * Scope (T-1.3.1 only):
 *   - Render one draggable cube shape item (data-testid="shape-cube").
 *   - On dragstart, write `{"shape":"cube"}` JSON to dataTransfer as
 *     'text/plain' so the MainCanvas drop handler (T-1.3.3) can read it.
 *
 * Out of scope (downstream tasks):
 *   - Rendering any voxels (T-1.3.2 ~ T-1.3.6).
 *   - PlaceVoxel command logic (T-1.3.5).
 *   - Multiple shape kinds (M5 nets).
 *
 * Contract (frozen for T-1.3.x hand-off):
 *   - dataTransfer key:   'text/plain'
 *   - dataTransfer value: JSON.stringify({ shape: 'cube' })
 *
 * The panel is intentionally a pure presentational component — it does
 * not hold MainScene refs, does not touch sceneStore, and does not
 * register any global event handlers. It is a leaf UI component
 * (Constitution §2.3: 不持有 MainScene ref).
 */

import type { DragEvent, JSX } from 'react';

/** Payload written to dataTransfer on dragstart. Open to future shapes. */
export interface ShapeDragPayload {
  shape: 'cube' | string;
}

/** MIME type used for the drag payload. */
export const SHAPE_DRAG_MIME = 'text/plain';

export function ShapePanel(): JSX.Element {
  const handleDragStart = (e: DragEvent<HTMLDivElement>): void => {
    const payload: ShapeDragPayload = { shape: 'cube' };
    e.dataTransfer.setData(SHAPE_DRAG_MIME, JSON.stringify(payload));
  };

  return (
    <aside
      aria-label="图形栏"
      data-testid="shape-panel"
      style={{
        padding: '1rem',
        borderRight: '1px solid #222',
        background: '#111',
        fontSize: '0.9rem',
        overflowY: 'auto',
      }}
    >
      <h2
        style={{
          margin: '0 0 0.75rem',
          fontSize: '1.05rem',
          color: '#e8e8e8',
        }}
      >
        立体图形
      </h2>
      <div
        data-testid="shape-cube"
        draggable
        onDragStart={handleDragStart}
        style={{
          padding: '0.75rem',
          background: '#1a1a1a',
          border: '1px solid #333',
          borderRadius: '4px',
          cursor: 'grab',
          userSelect: 'none',
          color: '#e8e8e8',
          textAlign: 'center',
          fontSize: '0.9rem',
        }}
        title="拖拽到场景中生成立方体"
      >
        🟦 立方体
      </div>
    </aside>
  );
}
